import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import {
  MoreVert,
  PlayArrowOutlined,
  PauseOutlined,
  EditOutlined,
  DeleteOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { KeywordMonitor } from "../types";
import PlatformIcon from "./PlatformIcon";
import {
  KEYWORD_MONITOR_STATUS_ACTIVE,
  KEYWORD_MONITOR_STATUS_PAUSED,
} from "../../../constants";
import moment from "moment";

interface MonitorCardProps {
  monitor: KeywordMonitor;
  onEdit: (monitor: KeywordMonitor) => void;
  onDelete: (monitor: KeywordMonitor) => void;
  onPause: (monitor: KeywordMonitor) => void;
  onResume: (monitor: KeywordMonitor) => void;
  onRunNow: (monitor: KeywordMonitor) => void;
  onClick: (monitor: KeywordMonitor) => void;
}

const MonitorCard: React.FC<MonitorCardProps> = ({
  monitor,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onRunNow,
  onClick,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const isActive = monitor.status === KEYWORD_MONITOR_STATUS_ACTIVE;

  return (
    <Card
      sx={{
        cursor: "pointer",
        transition: "box-shadow 0.2s",
        "&:hover": {
          boxShadow: 4,
        },
      }}
      onClick={() => onClick(monitor)}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {monitor.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip
                size="small"
                label={isActive ? "Active" : "Paused"}
                color={isActive ? "primary" : "default"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              {monitor.platforms.map((platform) => (
                <PlatformIcon key={platform} platform={platform} size={16} />
              ))}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{ ml: 1 }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={() => handleAction(() => onRunNow(monitor))}>
              <ListItemIcon>
                <RefreshOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Run Now</ListItemText>
            </MenuItem>
            {isActive ? (
              <MenuItem onClick={() => handleAction(() => onPause(monitor))}>
                <ListItemIcon>
                  <PauseOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Pause</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleAction(() => onResume(monitor))}>
                <ListItemIcon>
                  <PlayArrowOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Resume</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => handleAction(() => onEdit(monitor))}>
              <ListItemIcon>
                <EditOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => handleAction(() => onDelete(monitor))}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <DeleteOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Keywords
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              height: 52,
              overflow: "hidden",
            }}
          >
            {monitor.keywords.slice(0, 5).map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
            {monitor.keywords.length > 5 && (
              <Chip
                label={`+${monitor.keywords.length - 5}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {monitor.totalMentionsFound} mentions found
          </Typography>
          {monitor.lastRunAt && (
            <Typography variant="caption" color="text.secondary">
              Last run {moment(monitor.lastRunAt).fromNow()}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonitorCard;
